import { z } from 'zod';

import { pageSchema } from '../api/pagination';
import { FAILURE_CODES, NOTE_STATUSES } from './status';

/** An earlier note this upload resembles. A warning, never an action - the upload still
 * succeeds (`backend/app/api/notes.py`'s `DuplicateResponse`). */
const duplicateResponseSchema = z.object({
  note_id: z.string(),
  kind: z.enum(['exact', 'similar']),
  created_at: z.string(),
  distance: z.number(),
});

export type DuplicateResponse = z.infer<typeof duplicateResponseSchema>;

/** What was stored, as the web app sees it (`backend/app/api/notes.py`'s `ImageResponse`). */
const imageResponseSchema = z.object({
  id: z.string(),
  content_type: z.string(),
  byte_size: z.number(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  sha256: z.string(),
  perceptual_hash: z.string().nullable(),
});

/**
 * Mirrors `backend/app/api/notes.py`'s `NoteResponse` - the body of both `201` (first success)
 * and `200` (idempotent replay) from `POST /notes`. The two are shown identically to the owner
 * (`docs/implementation/06-mobile-web.md` 6.2: "a `201` response displayed as submitted"), so
 * nothing in this shape carries the HTTP status.
 */
export const noteResponseSchema = z.object({
  id: z.string(),
  status: z.enum(NOTE_STATUSES),
  created_at: z.string(),
  captured_at: z.string().nullable(),
  image: imageResponseSchema,
  duplicates: z.array(duplicateResponseSchema),
});

export type NoteResponse = z.infer<typeof noteResponseSchema>;

/**
 * Mirrors `backend/app/api/lists.py`'s `NoteSummaryResponse` - one row of `GET /notes`.
 * `customer`, `note_date`, `line_count`, `written_total`, `calculated_total`, and `revision_no`
 * are deliberately absent (`null`/`0`) before a revision exists, or when a revision's document
 * is too new a `schema_version` for this build to parse: `backend/app/reporting/notes.py` is
 * explicit that a note the owner cannot see is worse than a note without a total beside it, so
 * the row survives with its stored facts and no derived ones rather than dropping out of the
 * page. Money fields are decimal strings, never numbers (CLAUDE.md: no binary floats for money).
 */
export const noteSummaryResponseSchema = z.object({
  id: z.string(),
  status: z.enum(NOTE_STATUSES),
  created_at: z.string(),
  captured_at: z.string().nullable(),
  /** When the owner discarded this note, or `null` while it is live work. `status` stays what
   * the note was when it was thrown away, so a discarded row can still say where it stood. */
  discarded_at: z.string().nullable(),
  discarded_reason: z.string().nullable(),
  failure_code: z.enum(FAILURE_CODES).nullable(),
  image_id: z.string().nullable(),
  customer: z.string().nullable(),
  customer_is_resolved: z.boolean(),
  note_date: z.string().nullable(),
  /** The pad's pre-printed serial number (`COTIZACION No.`), for matching a row here against
   * the sheet of paper it came from. Internal to NoteVision: it never reaches Excel. */
  folio: z.string().nullable(),
  line_count: z.number(),
  written_total: z.string().nullable(),
  calculated_total: z.string().nullable(),
  revision_no: z.number().nullable(),
  open_reference_issues: z.number(),
  unresolved_alerts: z.number(),
});

export type NoteSummary = z.infer<typeof noteSummaryResponseSchema>;

/** One page of `GET /notes` - see `app/api/pagination.ts` for the cursor contract. */
export const notesPageSchema = pageSchema(noteSummaryResponseSchema);

/** Mirrors `backend/app/api/retry.py`'s `RetryResponse` - the body of `POST /notes/{id}/retry`. */
export const retryResponseSchema = z.object({
  note_id: z.string(),
  status: z.enum(NOTE_STATUSES),
  recognition_job_id: z.string(),
});

export type RetryResponse = z.infer<typeof retryResponseSchema>;

/** Mirrors `backend/app/api/discard.py`'s `DiscardResponse`. `status` is what the note *was*:
 * discarding never moves it, so a restore can put it back into the same queue. */
export const discardResponseSchema = z.object({
  note_id: z.string(),
  status: z.enum(NOTE_STATUSES),
  discarded_at: z.string(),
  discarded_reason: z.string().nullable(),
  cancelled_job_ids: z.array(z.string()),
  cancelled_delivery_ids: z.array(z.string()),
});

export type DiscardResponse = z.infer<typeof discardResponseSchema>;

/** Mirrors `backend/app/api/discard.py`'s `RestoreResponse` - what the restore re-queued. */
export const restoreResponseSchema = z.object({
  note_id: z.string(),
  status: z.enum(NOTE_STATUSES),
  recognition_job_id: z.string().nullable(),
  delivery_job_id: z.string().nullable(),
  reopened_delivery_ids: z.array(z.string()),
});

export type RestoreResponse = z.infer<typeof restoreResponseSchema>;
